/**
 * Shared type definitions for the session module.
 *
 * These types were originally co-located with the SessionState class.
 * They are now extracted here so that consumers can import types
 * without depending on the (deprecated) SessionState class.
 */

import {SessionParticipant} from './SessionParticipant';
import {SessionVideoQuality} from './SessionVideoQuality';

// ── Re-exports from sibling models ──────────────────────────────────────────
export {SessionParticipant} from './SessionParticipant';
export {ViewMode} from './ViewMode';
export {ZoomOptions} from './ZoomOptions';
export {SessionCapabilities} from './SessionCapabilities';
export {TimerPayload} from './TimerPayload';

// ── Scalar type aliases ─────────────────────────────────────────────────────

export type UserIdType = number | string;

// ── Media device types ──────────────────────────────────────────────────────

export interface MediaPlaybackFile {
  /** Media playback file URL. */
  url: string;
  /** Start time of file playback. */
  currentTime?: number;
  /** Is loop */
  loop?: boolean;
  /** Whether to play the audio file locally */
  playback?: boolean;
}

export interface MediaDevice {
  /** Device label. */
  label: string;
  /** Device ID. */
  deviceId: string;
}

// ── Device-started payloads ─────────────────────────────────────────────────

export type DeviceStarted = {
  device?: MediaDevice
  started: boolean
}

export type AudioStarted = DeviceStarted & {
  muted?: boolean
}

// ── Video / canvas dimensions ───────────────────────────────────────────────

export type VideoDimension = {
  width: number,
  height: number
}

export type VideoCanvasDimension = VideoDimension & {
  canvas?: HTMLCanvasElement,
  volume?: number; // Volume level from 0.0 to 1.0
}

// ── Render / attach / detach payloads ───────────────────────────────────────

export type StopRenderParticipant = {
  participant: SessionParticipant,
  canvas: HTMLCanvasElement
}

export type RenderParticipant = {
  participant: SessionParticipant,
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  x: number,
  y: number,
  quality?: SessionVideoQuality,
  newCanvasDimension?: VideoDimension
}

export type SupportedVideoElement = HTMLVideoElement;
export type SupportedVideoElementType = 'video' | 'video-player';

export type AttachVideoRequest = {
  userId: UserIdType,
  quality?: SessionVideoQuality,
  elementType: SupportedVideoElementType,
  element?: SupportedVideoElement,
}

export type DetachVideoRequest = {
  userId: UserIdType,
  element: SupportedVideoElement,
}

export type AttachedVideoUpdate = {
  userId: UserIdType,
  element: HTMLElement
}

// ── Media / playback types ──────────────────────────────────────────────────

export type MediaPlayback = {
  started: boolean,
  file?: MediaPlaybackFile
  title?: string
}

export type MusicPlayback = {
  started: boolean,
  paused?: boolean,
  mp3List?: string[],
  volume?: number,
  currentTrack?: string
}

export type MediaState = {
  audioEncode: boolean
  audioDecode: boolean
  videoEncode: boolean
  videoDecode: boolean
  success: boolean
}

// ── Instruction video ───────────────────────────────────────────────────────

export type InstructionVideo = {
  url?: string,
  maximized?: boolean,
  attempt?: number,
  enabled: boolean,
}

// ── Connection / network types ──────────────────────────────────────────────

export enum ConnectionStatus {
  Connected,
  Failed,
  Disconnected,
  Reconnecting
}

export type ConnectionChange = {
  status: ConnectionStatus
  reason?: string
}

export type NetworkQualityPayload = {
  /** User ID. */
  userId: number;
  /** Uplink or downlink. */
  type: 'uplink' | 'downlink';
  /**
   * Level
   * 0,1: bad
   * 2: normal
   * 3,4,5: good
   */
  level: number;
  currentTrack?: string;
}

// ── Session initialization ──────────────────────────────────────────────────

export class SessionInitialization {
  useSelfVideoElement: boolean
}
